import { useCallback, useEffect, useRef, useState } from "react";
import { Notebook, PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, CURRENCIES, fmtErr, inr, monthStart, STATES, today } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

const emptyForm = { name: "", gstin: "", state: "Maharashtra", phone: "", email: "", address: "", district: "", pin_code: "", opening_balance: "0", currency: "INR" };

function LedgerDialog({ party, onClose }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    if (!party) return;
    try {
      const params = new URLSearchParams();
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      const r = await API.get(`/parties/${party.id}/ledger?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [party, from, to]);

  useEffect(() => {
    setFrom("");
    setTo("");
    setData(null);
  }, [party]);

  useEffect(() => {
    load();
  }, [load]);

  if (!party) return null;

  return (
    <Dialog open={!!party} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="ledger-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Ledger — {party.name}</DialogTitle>
          <DialogDescription className="sr-only">Party ledger with running balance</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ledger-from-input" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ledger-to-input" />
          </div>
          <Button variant="outline" size="sm" onClick={() => { setFrom(""); setTo(""); }} data-testid="ledger-clear-button">All time</Button>
        </div>
        {data && (
          <>
            <div className="rounded-md border">
              <Table data-testid="ledger-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Particulars</TableHead>
                    <TableHead>Voucher</TableHead>
                    <TableHead className="text-right">Debit (₹)</TableHead>
                    <TableHead className="text-right">Credit (₹)</TableHead>
                    <TableHead className="text-right">Balance (₹)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <TableRow className="bg-muted/40">
                    <TableCell colSpan={5} className="font-medium">Opening Balance</TableCell>
                    <TableCell className="text-right font-medium tabular-nums" data-testid="ledger-opening">{inr(data.opening)}</TableCell>
                  </TableRow>
                  {data.entries.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{e.date}</TableCell>
                      <TableCell>{e.particulars}</TableCell>
                      <TableCell>{e.voucher_no}</TableCell>
                      <TableCell className="text-right tabular-nums">{e.debit ? inr(e.debit) : ""}</TableCell>
                      <TableCell className="text-right tabular-nums">{e.credit ? inr(e.credit) : ""}</TableCell>
                      <TableCell className="text-right tabular-nums">{inr(e.balance)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-muted/40">
                    <TableCell colSpan={5} className="font-heading font-bold">Closing Balance</TableCell>
                    <TableCell className="text-right font-heading font-bold tabular-nums" data-testid="ledger-closing">{inr(data.closing)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
            <p className="text-xs text-muted-foreground">
              {data.closing >= 0 ? "Positive balance = party owes you" : "Negative balance = you owe the party"}
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Parties() {
  const [tab, setTab] = useState("customer");
  const [parties, setParties] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [ledgerParty, setLedgerParty] = useState(null);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqRef.current;
    try {
      const r = await API.get(`/parties?type=${tab}`);
      if (reqId === reqRef.current) setParties(r.data);
    } catch (e) {
      if (reqId === reqRef.current) toast.error(fmtErr(e));
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, gstin: p.gstin || "", state: p.state || "Maharashtra",
      phone: p.phone || "", email: p.email || "", address: p.address || "",
      district: p.district || "", pin_code: p.pin_code || "",
      opening_balance: String(p.opening_balance ?? 0),
      currency: p.currency || "INR",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (form.pin_code && !/^\d{6}$/.test(form.pin_code)) return toast.error("PIN code must be 6 digits");
    setSaving(true);
    const payload = { ...form, type: tab, currency: tab === "customer" ? form.currency : "INR", opening_balance: Number(form.opening_balance) || 0 };
    try {
      if (editing) {
        await API.put(`/parties/${editing.id}`, payload);
        toast.success("Party updated");
      } else {
        await API.post("/parties", payload);
        toast.success("Party added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this party?")) return;
    try {
      await API.delete(`/parties/${id}`);
      toast.success("Party deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="parties-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Parties</h1>
          <p className="text-sm text-muted-foreground">Customers &amp; vendors with ledgers and outstanding balances</p>
        </div>
        <Button onClick={openNew} data-testid="new-party-button"><Plus size={16} className="mr-1" weight="bold" /> New {tab === "customer" ? "Customer" : "Vendor"}</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="party-tabs">
          <TabsTrigger value="customer" data-testid="customers-tab">Customers</TabsTrigger>
          <TabsTrigger value="vendor" data-testid="vendors-tab">Vendors</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="rounded-lg border bg-card">
        {parties.length === 0 ? (
          <div className="p-12 text-center" data-testid="empty-state">
            <p className="font-heading text-lg font-semibold">No {tab}s yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Add your first {tab} to start creating {tab === "customer" ? "invoices" : "purchase bills"}.</p>
          </div>
        ) : (
          <Table data-testid="parties-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {parties.map((p) => (
                <TableRow key={p.id} className="transition-colors hover:bg-muted/50" data-testid={`party-row-${p.name}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="font-mono text-xs">{p.gstin || "—"}</TableCell>
                  <TableCell>{p.state}</TableCell>
                  <TableCell>{p.phone || "—"}</TableCell>
                  <TableCell className={`text-right font-medium tabular-nums ${p.balance > 0 ? "text-[#008A52]" : p.balance < 0 ? "text-[#E02424]" : ""}`} data-testid={`party-balance-${p.name}`}>
                    {inr(p.balance)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" title="Ledger" onClick={() => setLedgerParty(p)} data-testid={`party-ledger-${p.name}`}>
                        <Notebook size={16} className="text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(p)} data-testid={`party-edit-${p.name}`}>
                        <PencilSimple size={16} className="text-muted-foreground" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => del(p.id)} data-testid={`party-delete-${p.name}`}>
                        <Trash size={16} className="text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="party-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">{editing ? "Edit" : "New"} {tab === "customer" ? "Customer" : "Vendor"}</DialogTitle>
            <DialogDescription className="sr-only">Party details form</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={set("name")} placeholder="Business / person name" data-testid="party-name-input" />
            </div>
            <div className="space-y-2">
              <Label>GSTIN</Label>
              <Input value={form.gstin} onChange={set("gstin")} placeholder="15-digit GSTIN (optional)" data-testid="party-gstin-input" />
            </div>
            <div className="space-y-2">
              <Label>State</Label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="party-state-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {tab === "customer" && (
              <div className="space-y-2">
                <Label>Default invoice currency</Label>
                <Select value={form.currency} onValueChange={set("currency")}>
                  <SelectTrigger data-testid="party-currency-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((currency) => <SelectItem key={currency.code} value={currency.code}>{currency.name} ({currency.code})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={set("phone")} data-testid="party-phone-input" />
            </div>
            <div className="space-y-2">
              <Label>Email (invoice recipient)</Label>
              <Input type="email" value={form.email} onChange={set("email")} data-testid="party-email-input" />
              {tab === "customer" && <p className="text-xs text-muted-foreground">Sales invoices and estimates are emailed to this address.</p>}
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Address line</Label>
              <Input value={form.address} onChange={set("address")} data-testid="party-address-input" />
            </div>
            <div className="space-y-2">
              <Label>District</Label>
              <Input value={form.district} onChange={set("district")} data-testid="party-district-input" />
            </div>
            <div className="space-y-2">
              <Label>PIN code</Label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={6}
                pattern="[0-9]{6}"
                value={form.pin_code}
                onChange={set("pin_code")}
                placeholder="6-digit PIN code"
                data-testid="party-pin-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Opening Balance (₹)</Label>
              <Input type="number" step="any" value={form.opening_balance} onChange={set("opening_balance")} data-testid="party-opening-input" />
              <p className="text-xs text-muted-foreground">Positive = they owe you, negative = you owe them</p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="party-cancel-button">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="party-save-button">{saving ? "Saving…" : editing ? "Update" : "Add Party"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <LedgerDialog party={ledgerParty} onClose={() => setLedgerParty(null)} />
    </div>
  );
}
